import EventEmitter from 'events'
import { setTinyDragPreview } from '@/etc/dnd.preview'

const dndBus = new EventEmitter()
dndBus.map = {}

const eData = (e, store) => {
  const jdata = e.dataTransfer.getData('text/plain')
  let data = null
  if (typeof jdata === 'string' && jdata.length) {
    try {
      data = JSON.parse(jdata)
    } catch(err) {
      data = store && store.state.ui ? store.state.ui.dnd : {}
    }
  } else {
    data = store && store.state.ui ? store.state.ui.dnd : {}
  }
  return JSON.parse(JSON.stringify(data || {}))
}

export const dndDrag = (store) => ({
  mounted(el, binding) {
    el.draggable = true
    el.addEventListener('dragstart', (e) => {
      e.stopPropagation()
      const tdata = JSON.stringify(binding.value || {})
      const data = JSON.parse(tdata)
      e.dataTransfer.setData('text/plain', tdata)
      e.dataTransfer.dropEffect = 'link'
      setTinyDragPreview(e, el, { label: data?.type || '' })
      if (store && store.state.ui)
        store.commit('ui/dnd',data)
      const { offsetX, offsetY } = e
      // console.log(e)
      dndBus.emit('start', { dnd: 'start', ...(data || {}), offsetX, offsetY  })
    })
    el.addEventListener('dragend', (e) => {
      e.stopPropagation()
      const data = eData(e, store)
      const { offsetX, offsetY } = e
      dndBus.emit('end', { dnd: 'end', ...data, offsetX, offsetY })
      if (store && store.state.ui)
        store.commit('ui/dnd', null)
    })
 
  }
})

export const dndDrop = (store) => ({
  mounted(el, binding) {
    el.draggable = true
    el.addEventListener('drop', (e) => {
      e.stopPropagation()
      const data = eData(e, store)
      const accept = Object.keys(binding.modifiers)
      let accepted = true
      if (accept.length) {
        if (!data.type || !accept.includes(data.type)) accepted = false
      }
      const { offsetX, offsetY } = e
      if (accepted && typeof binding.value === 'function')
        binding.value({ ...data, offsetX, offsetY })
      dndBus.emit('drop', { dnd: 'drop', el, data, accepted, offsetX, offsetY })
    })
  }
})

export const dndListen = () => ({
  mounted(el, binding) {
    const hash = el.getAttribute('id')
    if (!hash || hash.includes('undefined')) {
      console.warn('dnd-listen require unique id attribute to be defined', el)
      return
    }
    const args = (binding.arg || 'start:end:drop:enter:leave:over').split(':')
    const accept = Object.keys(binding.modifiers)
    if (!el.dndListen) el.dndListen = {}
    if (!el.dndHandlers) el.dndHandlers = {} // Store actual handlers for removal

    args.forEach(key => {
      if (!el.dndListen[key]) {
        el.dndListen[key] = (data) => {
          if (accept.length && (!data || !data.type || !accept.includes(data.type)))
            return
          if (typeof binding.value === 'function')
            binding.value(data)
        }
      }
    })

    args.forEach(e => {
      if (!dndBus.map[hash])
        dndBus.map[hash] = []
      
      // Always create a new handler bound to this element instance
      // Store it so we can remove exactly this function later
      const handler = (x) => { 
        if (el.dndListen && el.dndListen[e]) el.dndListen[e](x)
      }
      
      if (!el.dndHandlers[e]) {
        el.dndHandlers[e] = handler
        dndBus.on(e, handler)
        // We don't strictly need dndBus.map to track event names anymore if we store handlers on el,
        // but keeping it for now to match existing pattern if used elsewhere.
        if (!dndBus.map[hash].includes(e)) {
          dndBus.map[hash].push(e)
        }
      }
    })
  },
  beforeUnmount(el) {
    if (el.dndHandlers) {
      const hash = el.getAttribute('id')
      if (hash && dndBus.map[hash]) delete dndBus.map[hash]
      
      Object.keys(el.dndHandlers).forEach(key => {
        const handler = el.dndHandlers[key]
        if (handler) {
          dndBus.off(key, handler)
        }
      })
      el.dndHandlers = null
    }
  }
})

export const dndEnter = (store) => ({
  mounted(el, binding) {
    const accept = Object.keys(binding.modifiers)
    el.addEventListener('dragenter', (e) => {
      e.stopPropagation()
      const data = eData(e, store)
      let accepted = true
      if (accept.length && (!data.type || !accept.includes(data.type)))
        accepted = false
      const { offsetX, offsetY } = e
      if (accepted && typeof binding.value === 'function')
        binding.value({ ...data, offsetX, offsetY })
      dndBus.emit('enter', { dnd: 'enter', el, accepted, ...data, offsetX, offsetY })
    })
  }
})

export const dndLeave = (store) => ({
  mounted(el, binding) {
    const accept = Object.keys(binding.modifiers)
    el.addEventListener('dragleave', (e) => {
      e.stopPropagation()
      const data = eData(e, store)
      let accepted = true
      if (accept.length && (!data.type || !accept.includes(data.type)))
        accepted = false
      const { offsetX, offsetY } = e
      if (accepted && typeof binding.value === 'function')
        binding.value({ ...data, offsetX, offsetY })
      dndBus.emit('leave', { dnd: 'leave', el, accepted, ...data, offsetX, offsetY })
    })
  }
})

export const dndOver = (store) => ({
  mounted(el, binding) {
    const accept = Object.keys(binding.modifiers)
    el.addEventListener('dragover', (e) => {
      e.stopPropagation()
      e.preventDefault()
      const data = eData(e, store)
      let accepted = true
      if (accept.length && (!data.type || !accept.includes(data.type)))
        accepted = false
      const { offsetX, offsetY } = e
      if (accepted && typeof binding.value === 'function')
        binding.value({ ...data, offsetX, offsetY })
      dndBus.emit('over', { dnd: 'over', el, accepted, ...data, offsetX, offsetY })
    })
  }
})

export const DND = (app, store, prefix = 'dnd') => {
  app.directive(`${prefix}-drag`, dndDrag(store))
  app.directive(`${prefix}-drop`, dndDrop(store))
  app.directive(`${prefix}-listen`, dndListen(store))
  app.directive(`${prefix}-enter`, dndEnter(store))
  app.directive(`${prefix}-leave`, dndLeave(store))
  app.directive(`${prefix}-over`, dndOver(store))
  return app
}
